import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    Modal,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { get } from "lodash";
import React from "react";
import { useNavigate } from "react-router-dom";
import Autocomplete from "../../../common/js/components/Autocomplete";
import { Close, Search } from "../../../common/js/icons";
import { buildAddress } from "../utils/operations";

function getModalStyle() {
    const top = 50;
    const left = 50;

    return {
        top: `${top}%`,
        left: `${left}%`,
        transform: `translate(-${top}%, -${left}%)`,
    };
}

const StyledCard = styled(Card)({
    display: "flex",
    marginBottom: "40px",
    border: "1px solid #dddddd",
    boxShadow: "none",
    minHeight: "74px",
    backgroundColor: "#f4f4f4",
});

const StyledCardSelected = styled(Card)({
    display: "flex",
    marginBottom: "40px",
    border: "1px solid #2C99D2",
    boxShadow: "none",
});

const StyledDetails = styled("div")({
    width: "100%",
});

const StyledContent = styled(CardContent)({
    flex: "1 0 auto",
    height: "100%",
    padding: "16px !important",
});

const StyledInstruction = styled("div")({
    marginTop: "24px",
    marginBottom: "24px",
});

const StyledClose = styled("div")({
    "&:hover": {
        cursor: "pointer",
    },
});

const StyledPaper = styled("div")(({ theme }) => ({
    position: "absolute",
    width: theme.spacing(60),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3),
    borderRadius: "3px",
    "&:focus": {
        outline: "none",
    },
}));

const StyledPropertyAddy = styled(Typography)({
    textTransform: "uppercase",
    fontSize: "13px",
});

const PropertyUpdateModal = (props) => {
    const {
        searchFunction,
        selectProperty,
        removeSelectedProperty,
        autocompleteResults,
        propertySearchModalStatus,
        togglePropertySearchModal,
        handleChooseDisplayUpdateDialog,
        displayUpdateDialog,
        updatePropertyForListing,
        updatePropertyForComp,
        dealKey,
        listingKey,
        listingPropertyModal,
        formType,
        user,
        updatePropertyForConsulting,
        consultingDealKey,
        errors,
    } = props;

    const navigate = useNavigate();

    const handleUpdate = (value) => {
        handleChooseDisplayUpdateDialog(value);
    };

    const handleCancel = () => {
        togglePropertySearchModal(false);
        removeSelectedProperty();
    };

    const updatePropertyForForm = async (formType) => {
        let result;
        if (formType === "listing") {
            result = await updatePropertyForListing(
                listingKey,
                get(listingPropertyModal, "selectedProperty.propertyKey"),
                user
            );
            if (result.success) {
                navigate(`/comments/listing/${listingKey}`);
                navigate(`/listing/${listingKey}`);
            }
        } else if (formType === "comp") {
            result = await updatePropertyForComp(
                dealKey,
                get(listingPropertyModal, "selectedProperty.propertyKey"),
                user
            );
            if (result.success) {
                navigate(`/comments/comp/${dealKey}`);
                navigate(`/comp/${dealKey}`);
            }
        } else if (formType === "consulting") {
            updatePropertyForConsulting(
                consultingDealKey,
                get(listingPropertyModal, "selectedProperty.propertyKey"),
                user
            );
        }
        removeSelectedProperty();
    };

    return (
        <Grid
            container
            spacing={16}
            sx={{
                justifyContent: "space-between",
                alignItems: "center",
            }}
        >
            <Modal
                aria-labelledby="property-update-modal"
                aria-describedby="update-listing-property"
                open={propertySearchModalStatus.open}
                onClose={removeSelectedProperty}
            >
                <StyledPaper style={getModalStyle()}>
                    <h4 className="modal-title">Select a Property</h4>
                    <Grid container spacing={0}>
                        <Grid size="grow">
                            <Divider />
                        </Grid>
                    </Grid>
                    <React.Fragment>
                        <StyledInstruction>
                            <Typography variant="body1">
                                Search the KM database to find the property associated with your listing.
                            </Typography>
                        </StyledInstruction>
                        <Autocomplete
                            id="property"
                            label={"Property Name"}
                            error={get(errors, `propertyKey`, null)}
                            itemToString={(i) => (i == null ? "" : get(i, "propertyAttributes.propertyName", ""))}
                            onSelect={(p, b) => selectProperty(p)}
                            placeholder="Search by property name, address, etc."
                            searchFunction={(input) => searchFunction(input)}
                            startAdornment={<Search />}
                            suggestions={autocompleteResults}
                            options={{
                                key: "propertyKey",
                                display: (suggestion) => {
                                    return get(suggestion, "propertyAttributes.propertyName", "");
                                },
                                displaySub: (suggestion) => {
                                    return get(suggestion, "primaryAddress.address1", "");
                                },
                            }}
                        />
                        &nbsp;
                        {listingPropertyModal.selectedProperty.propertyKey ? (
                            <StyledCardSelected>
                                <StyledDetails>
                                    <StyledContent>
                                        <Grid
                                            container
                                            spacing={0}
                                            sx={{
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <Grid size="grow">
                                                <Typography component="h4" variant="h4">
                                                    {get(
                                                        listingPropertyModal,
                                                        "selectedProperty.propertyAttributes.propertyName"
                                                    )}
                                                </Typography>
                                                <StyledPropertyAddy variant="body2" color="textSecondary">
                                                    {buildAddress(
                                                        get(listingPropertyModal, "selectedProperty.primaryAddress", {})
                                                    )}
                                                </StyledPropertyAddy>
                                            </Grid>
                                            <Grid>
                                                <StyledClose onClick={removeSelectedProperty}>
                                                    <Close />
                                                </StyledClose>
                                            </Grid>
                                        </Grid>
                                    </StyledContent>
                                </StyledDetails>
                            </StyledCardSelected>
                        ) : (
                            <StyledCard></StyledCard>
                        )}
                    </React.Fragment>

                    <Grid
                        container
                        sx={{
                            justifyContent: "flex-end",
                        }}
                    >
                        <Grid>
                            <Button variant="outlined" onClick={() => handleCancel()}>
                                Cancel
                            </Button>
                        </Grid>
                        <Grid>
                            <Button variant="contained" color="primary" onClick={() => handleUpdate(true)}>
                                Update
                            </Button>
                        </Grid>
                    </Grid>
                </StyledPaper>
            </Modal>
            {formType === "consulting" ? (
                <Dialog open={displayUpdateDialog.open} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                    <DialogTitle id="confirmation-dialog-title">
                        Are you sure you want to update the Property Address you have selected?
                    </DialogTitle>
                    <DialogActions>
                        <Button onClick={() => handleUpdate(false)} color="primary">
                            No
                        </Button>
                        <Button onClick={() => updatePropertyForForm(formType)} color="primary">
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : formType === "listing" ? (
                <Dialog open={displayUpdateDialog.open} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                    <DialogTitle id="confirmation-dialog-title">
                        Are you sure you want to move this Listing to the Property you have selected?
                    </DialogTitle>
                    <DialogContent>
                        If there is a Property Request that is only associated with this Listing, it will be deleted. If
                        the original Property associated with the Listing already exists, it will remain unchanged
                        outside of this update.
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handleUpdate(false)} color="primary">
                            No
                        </Button>
                        <Button onClick={() => updatePropertyForForm(formType)} color="primary">
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : (
                <Dialog open={displayUpdateDialog.open} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                    <DialogTitle id="confirmation-dialog-title">
                        Are you sure you want to move this Comp to the Property you have selected?
                    </DialogTitle>
                    <DialogContent>
                        If there is a Property Request that is only associated with this Comp, it will be deleted. If
                        the original Property associated with the Comp already exists, it will remain unchanged outside
                        of this update.
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => handleUpdate(false)} color="primary">
                            No
                        </Button>
                        <Button onClick={() => updatePropertyForForm(formType)} color="primary">
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Grid>
    );
};

export default PropertyUpdateModal;
